<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          An individual's full name (no abbreviations or nicknames), middle name
          or initial (if you use this on official documents), and suffix which
          matches legal documents like a driver's license or passport.
        </p>
      </div>
    </help-drawer>
    <v-form name="LegalName" class="form" data-test-id="legal-name-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.LEGAL_NAME.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="legal-name-header">
              <span class="headerlight">
                What's your <strong>legal name?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your legal name...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isLegalNameComplete && formHidden && !isSyncing"
          :submitted="isLegalNameComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container">
            <label for="firstName" class="required" data-test-id="first-name-label">First Name</label>
            <v-text-field
              outlined
              type="text"
              maxlength="256"
              name="firstName"
              aria-required="true"
              v-model.trim="firstName"
              :backgroundColor="inputBackgroundColor"
              @input="$v.firstName.$touch()"
              @blur="$v.firstName.$touch()"
              :error-messages="firstNameErrors"
              hide-details="auto"
              class="mb-2"
              data-test-id="first-name-input"
            />
            <label for="lastName" class="required" data-test-id="last-name-label">Last Name</label>
            <v-text-field
              outlined
              type="text"
              maxlength="256"
              name="lastName"
              aria-required="true"
              v-model.trim="lastName"
              :backgroundColor="inputBackgroundColor"
              @input="$v.lastName.$touch()"
              @blur="$v.lastName.$touch()"
              :error-messages="lastNameErrors"
              hide-details="auto"
              class="mb-2"
              data-test-id="last-name-input"
            />
            <v-row>
              <v-col>
                <label for="middleName" data-test-id="middle-name-label">Middle Name</label>
                <v-text-field
                  outlined
                  type="text"
                  maxlength="256"
                  name="middleName"
                  v-model.trim="middleName"
                  :backgroundColor="inputBackgroundColor"
                  @input="$v.middleName.$touch()"
                  @blur="$v.middleName.$touch()"
                  hide-details="auto"
                  data-test-id="middle-name-input"
                />
              </v-col>
              <v-col>
                <label for="suffix" data-test-id="suffix-label">Suffix</label>
                <v-select
                  v-model="suffix"
                  ref="suffix"
                  :items="suffixes"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  placeholder="Choose"
                  dense
                  hide-details="auto"
                  @change="$v.suffix.$touch()"
                  data-test-id="suffix-select"
                />
              </v-col>
            </v-row>
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="legal-name-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, SUFFIXES } from '@/common/constants';
import { required } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'LegalName',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    firstName: null,
    lastName: null,
    middleName: null,
    suffix: null,
    MENUS: MENUS,
    SUFFIXES: SUFFIXES,
  }),
  validations() {
    return {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      middleName: {},
      suffix: {},
      validationGroup: ['firstName', 'lastName', 'middleName', 'suffix'],
    };
  },
  computed: {
    // Is Legal Name complete
    isLegalNameComplete() {
      return this.isPageComplete(MENUS.CLIENT.LEGAL_NAME.id);
    },
    
    // Dynamic First Name errors */
    firstNameErrors() {
      const errors = [];
      if (!this.$v.firstName.$dirty) return errors;
      !this.$v.firstName.required && errors.push('First Name is required');
      return errors;
    },

    // Dynamic Last Name errors */
    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      !this.$v.lastName.required && errors.push('Last Name is required');
      return errors;
    },
    helpTitle() {
      return 'What does this mean?';
    },
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.LEGAL_NAME.id);
      return page && page.status === 'IN_PROGRESS';
    },
  },
  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        firstName: this.firstName,
        lastName: this.lastName,
        middleName: this.middleName,
        suffix: this.suffix,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isLegalNameComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isLegalNameComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    // Initialize form data for current interview
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.LEGAL_NAME.id);

    this.formHidden = this.isLegalNameComplete;
    this.suffixes = SUFFIXES;
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If legal name is complete, hide the form
      if (this.isLegalNameComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
